import { useState, useEffect } from 'react';
import { X, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import {
  fetchEquipmentsByCategory,
  fetchApplicableEquipmentsByCategory,
  addImprovementToScenario,
} from '../../services/work-scenarios-service';
import type { EquipmentsByCategory, ImprovementType } from '../../types/work-scenarios';
import { supabase } from '../../utils/supabase';
import { checkWallMaterialCompatibility } from '../../utils/work-recommendations-filter';

interface EquipmentSelectorProps {
  scenarioId: string;
  dpeId: string;
  onClose: () => void;
  onImprovementAdded: () => void;
}

interface DPEData {
  heating_energy?: string;
  hot_water_energy?: string;
}

interface UserAnswers {
  nature_murs?: string;
  [key: string]: any;
}

const CATEGORY_LABELS: Record<ImprovementType, string> = {
  windows: 'Fenêtres',
  wall_insulation: 'Isolation des murs',
  roof_insulation: 'Isolation de la toiture',
  floor_insulation: 'Isolation du plancher',
  heating: 'Système de chauffage',
  hot_water: 'Eau chaude sanitaire',
  ventilation: 'Ventilation',
  heating_control: 'Régulation du chauffage',
};

export function EquipmentSelector({
  scenarioId,
  dpeId,
  onClose,
  onImprovementAdded,
}: EquipmentSelectorProps) {
  const [equipmentsByCategory, setEquipmentsByCategory] = useState<EquipmentsByCategory | null>(
    null
  );
  const [blockedCategories, setBlockedCategories] = useState<Record<string, string[]>>({});
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<ImprovementType>('windows');
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useFiltering, setUseFiltering] = useState(true);
  const [dpeData, setDpeData] = useState<DPEData | null>(null);
  const [userAnswers, setUserAnswers] = useState<UserAnswers | null>(null);

  useEffect(() => {
    loadEquipment();
    loadDPEData();
    loadUserAnswers();
  }, [useFiltering]);

  async function loadDPEData() {
    try {
      const { data, error } = await supabase
        .from('dpe_data')
        .select('heating_energy, hot_water_energy')
        .eq('id', dpeId)
        .maybeSingle();

      if (!error && data) {
        setDpeData(data);
      }
    } catch (err) {
      console.error('Failed to load DPE data:', err);
    }
  }

  async function loadUserAnswers() {
    try {
      const { data, error } = await supabase
        .from('dpe_analyses')
        .select('answers')
        .eq('dpe_id', dpeId)
        .maybeSingle();

      if (!error && data?.answers) {
        const parsedAnswers: UserAnswers = {};
        if (Array.isArray(data.answers)) {
          for (const answer of data.answers) {
            parsedAnswers[answer.question_id] = answer.selected_option || answer.selected_option_id;
          }
        }
        setUserAnswers(parsedAnswers);
        console.log('🔍 [EQUIPMENT SELECTOR] User answers loaded:', parsedAnswers);
      }
    } catch (err) {
      console.error('Failed to load user answers:', err);
    }
  }

  async function loadEquipment() {
  try {
    setIsLoading(true);
    console.log('🚀 [EQUIPMENT SELECTOR] Loading equipment with filtering:', useFiltering, 'DPE:', dpeId);

    if (useFiltering) {
      // Utiliser le nouveau système avec filtrage intelligent
      console.log('🔍 [EQUIPMENT SELECTOR] Fetching applicable equipments...');
      const data = await fetchApplicableEquipmentsByCategory(dpeId);
      console.log('✅ [EQUIPMENT SELECTOR] Data received:', {
        recommendationsCount: data.recommendations.length,
        blockedCategoriesCount: Object.keys(data.blockedCategories).length,
        equipmentCategories: Object.keys(data.equipments).map(cat => ({
          category: cat,
          count: data.equipments[cat as keyof typeof data.equipments].length
        }))
      });

      // ✅ NOUVEAU : Filtrer les recommandations de chauffage/ECS si collectif
      const filteredRecommendations = data.recommendations.filter(rec => {
        // Vérifier si chauffage collectif dans les blockedCategories
        const isHeatingBlocked = data.blockedCategories['heating']?.some(
          blocker => blocker.toLowerCase().includes('collectif') || blocker.toLowerCase().includes('chauffage collectif')
        );
        
        const isHotWaterBlocked = data.blockedCategories['hot_water']?.some(
          blocker => blocker.toLowerCase().includes('collectif') || blocker.toLowerCase().includes('eau chaude')
        );

        // Filtrer les recommandations de chauffage si bloqué
        if (rec.category === 'heating' && isHeatingBlocked) {
          console.log('🚫 [EQUIPMENT SELECTOR] Filtering heating recommendation (collective system)');
          return false;
        }

        // Filtrer les recommandations ECS si bloqué
        if (rec.category === 'hot_water' && isHotWaterBlocked) {
          console.log('🚫 [EQUIPMENT SELECTOR] Filtering hot water recommendation (collective system)');
          return false;
        }

        return true;
      });

      // ✅ NOUVEAU : Vider les équipements des catégories bloquées
      const filteredEquipments = { ...data.equipments };
      if (data.blockedCategories['heating']?.length > 0) {
        filteredEquipments.heating = [];
      }
      if (data.blockedCategories['hot_water']?.length > 0) {
        filteredEquipments.hot_water = [];
      }

      setEquipmentsByCategory(filteredEquipments);
      setBlockedCategories(data.blockedCategories);
      setRecommendations(filteredRecommendations);

      // Sélectionner la première catégorie disponible
      const firstAvailable = (Object.keys(CATEGORY_LABELS) as ImprovementType[])
        .find(cat => filteredEquipments[cat].length > 0);
      if (firstAvailable) {
        setSelectedCategory(firstAvailable);
      }
    } else {
      // Fallback : afficher tous les équipements
      const data = await fetchEquipmentsByCategory();
      setEquipmentsByCategory(data);
      setBlockedCategories({});
      setRecommendations([]);
    }
  } catch (err) {
    setError(err instanceof Error ? err.message : 'Failed to load equipment');
  } finally {
    setIsLoading(false);
  }
}

  function isAirAirOnHydraulicNetwork(equipment: any): boolean {
    if (selectedCategory !== 'heating') return false;
    const heatingEnergy = (dpeData?.heating_energy || '').toLowerCase();
    const hasHydraulicNetwork =
      heatingEnergy.includes('gaz') || heatingEnergy.includes('fioul') || heatingEnergy.includes('fuel');
    if (!hasHydraulicNetwork) return false;
    const fullText = (equipment.name + ' ' + (equipment.description || '')).toLowerCase();
    return fullText.includes('air-air');
  }

  function isGasToElectricRadiators(equipment: any): boolean {
    if (!dpeData?.heating_energy || selectedCategory !== 'heating') {
      return false;
    }

    const currentEnergyType = dpeData.heating_energy;
    const isCurrentNonElectric =
      currentEnergyType.includes('gaz') ||
      currentEnergyType.includes('fioul') ||
      currentEnergyType.includes('bois');

    const equipmentName = equipment.name.toLowerCase();
    const isElectricRadiator =
      equipmentName.includes('radiateur') ||
      equipmentName.includes('convecteur') ||
      equipmentName.includes('panneau rayonnant');

    const isNotHeatPump = !equipment.cop || equipment.cop <= 1.1;

    return isCurrentNonElectric && isElectricRadiator && isNotHeatPump;
  }

  async function handleSelectEquipment(equipmentId: string) {
    try {
      setIsAdding(true);
      setError(null);

      await addImprovementToScenario({
        scenario_id: scenarioId,
        improvement_type: selectedCategory,
        equipment_standard_id: equipmentId,
      });

      onImprovementAdded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add improvement');
    } finally {
      setIsAdding(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden m-4 flex flex-col">
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">
              Ajouter des travaux
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <p className="mt-2 text-gray-600">
            Sélectionnez le type de travaux et l'équipement à installer
          </p>

          {useFiltering && recommendations.length > 0 && (
            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start">
                <Info className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <strong>Recommandations personnalisées :</strong> Les travaux affichés sont filtrés selon votre DPE et vos réponses au questionnaire.
                  <button
                    onClick={() => setUseFiltering(false)}
                    className="ml-2 underline hover:text-blue-900"
                  >
                    Voir tous les travaux
                  </button>
                </div>
              </div>
            </div>
          )}

          {!useFiltering && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-start">
                <Info className="w-5 h-5 text-gray-600 mt-0.5 mr-2 flex-shrink-0" />
                <div className="text-sm text-gray-700">
                  Mode tous les travaux activé.
                  <button
                    onClick={() => setUseFiltering(true)}
                    className="ml-2 underline hover:text-gray-900"
                  >
                    Revenir aux recommandations
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-1 min-h-0">
          <div className="w-64 border-r border-gray-200 overflow-y-auto flex-shrink-0">
            <nav className="p-4 space-y-1">
              {(Object.keys(CATEGORY_LABELS) as ImprovementType[]).map((category) => {
                const isBlocked = blockedCategories[category]?.length > 0;
                const hasEquipment = (equipmentsByCategory?.[category]?.length || 0) > 0;
                const recommendation = recommendations.find(rec => rec.category === category);

                return (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    disabled={!hasEquipment && useFiltering}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors relative ${
                      selectedCategory === category
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : hasEquipment
                        ? 'text-gray-700 hover:bg-gray-50'
                        : 'text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{CATEGORY_LABELS[category]}</span>
                      {useFiltering && (
                        <>
                          {hasEquipment && recommendation?.priority === 'high' && (
                            <Badge variant="error" size="sm">Prioritaire</Badge>
                          )}
                          {hasEquipment && recommendation?.priority === 'medium' && (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          )}
                          {isBlocked && (
                            <AlertCircle className="w-4 h-4 text-orange-500" />
                          )}
                        </>
                      )}
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <LoadingSpinner />
              </div>
            ) : error ? (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
                {error}
              </div>
            ) : blockedCategories[selectedCategory]?.length > 0 ? (
              <div className="space-y-4">
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-start">
                    <AlertCircle className="w-6 h-6 text-orange-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-orange-900 mb-2">
                        Ces travaux ne sont pas recommandés pour votre logement
                      </h3>
                      <p className="text-sm text-orange-800 mb-3">
                        Selon vos réponses au questionnaire et les données de votre DPE :
                      </p>
                      <ul className="space-y-2">
                        {blockedCategories[selectedCategory].map((blocker, idx) => (
                          <li key={idx} className="text-sm text-orange-800 flex items-start">
                            <span className="mr-2">•</span>
                            <span>{blocker}</span>
                          </li>
                        ))}
                      </ul>
                      {!useFiltering && (
                        <p className="mt-3 text-sm text-orange-700 italic">
                          Vous pouvez quand même ajouter ces travaux en mode "tous les travaux".
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (equipmentsByCategory?.[selectedCategory]?.length || 0) === 0 ? (
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center text-gray-600">
                Aucun équipement disponible pour cette catégorie
              </div>
            ) : (
              <div className="space-y-4">
                {recommendations.find(rec => rec.category === selectedCategory) && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
                    <div className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-green-900 mb-1">
                          Travaux recommandés
                        </h3>
                        <p className="text-sm text-green-800">
                          {recommendations.find(rec => rec.category === selectedCategory)?.reason}
                        </p>
                        {recommendations.find(rec => rec.category === selectedCategory)?.estimatedSavings && (
                          <p className="text-sm text-green-700 mt-2">
                            <strong>Économies estimées :</strong> {recommendations.find(rec => rec.category === selectedCategory)?.estimatedSavings} kWh/an
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {selectedCategory === 'wall_insulation' ? (
                  <div className="space-y-6">
                    {/* Section ITE */}
                    {(() => {
                      const iteEquipments = equipmentsByCategory?.[selectedCategory]?.filter(
                        (eq: any) => eq.insulation_location === 'exterior'
                      ) || [];

                      return iteEquipments.length > 0 ? (
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <h3 className="text-lg font-semibold text-gray-900">
                              Isolation par l'extérieur (ITE)
                            </h3>
                            <Badge variant="info" size="sm">Traite 75% des ponts thermiques</Badge>
                          </div>
                          <div className="space-y-3">
                            {iteEquipments.map((equipment: any) => {
                              const compatibility = checkWallMaterialCompatibility(equipment, userAnswers?.nature_murs);
                              return (
                                <button
                                  key={equipment.id}
                                  onClick={() => handleSelectEquipment(equipment.id)}
                                  disabled={isAdding}
                                  className={`w-full text-left p-4 border-2 rounded-lg hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                                    compatibility.level === 'danger'
                                      ? 'border-red-300 bg-red-50 hover:border-red-500'
                                      : 'border-gray-200 hover:border-blue-500'
                                  }`}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <h4 className="font-semibold text-gray-900">
                                          {equipment.name}
                                        </h4>
                                        {equipment.is_default && (
                                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                            Recommandé
                                          </span>
                                        )}
                                        {equipment.is_breathable === true && (
                                          <Info className="w-4 h-4 text-green-600" title="Matériau perspirant - Compatible avec tous types de murs" />
                                        )}
                                        {compatibility.level === 'danger' && (
                                          <AlertTriangle className="w-4 h-4 text-red-600" />
                                        )}
                                      </div>
                                      <p className="mt-1 text-sm text-gray-600">
                                        {equipment.description}
                                      </p>
                                      {compatibility.message && (
                                        <div className={`mt-2 p-2 rounded text-xs ${
                                          compatibility.level === 'danger'
                                            ? 'bg-red-100 text-red-800 border border-red-200'
                                            : 'bg-blue-50 text-blue-700'
                                        }`}>
                                          {compatibility.message}
                                        </div>
                                      )}
                                      <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500">
                                        {equipment.u_value !== null && (
                                          <span>U = {equipment.u_value.toFixed(2)} W/m².K</span>
                                        )}
                                        {equipment.r_value !== null && (
                                          <span>R = {equipment.r_value.toFixed(1)} m².K/W</span>
                                        )}
                                        {equipment.thickness_mm !== null && (
                                          <span>Épaisseur = {equipment.thickness_mm} mm</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ) : blockedCategories[selectedCategory]?.some(b => b.includes('ITE')) ? (
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm text-blue-800">
                            <strong>ITE non applicable :</strong> Contraintes urbanistiques (PLU) ou bâtiment classé
                          </p>
                        </div>
                      ) : null;
                    })()}

                    {/* Section ITI */}
                    {(() => {
                      const itiEquipments = equipmentsByCategory?.[selectedCategory]?.filter(
                        (eq: any) => eq.insulation_location === 'interior'
                      ) || [];

                      return itiEquipments.length > 0 ? (
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <h3 className="text-lg font-semibold text-gray-900">
                              Isolation par l'intérieur (ITI)
                            </h3>
                            <Badge variant="success" size="sm">Amélioration partielle des ponts thermiques</Badge>
                          </div>
                          <div className="space-y-3">
                            {itiEquipments.map((equipment: any) => {
                              const compatibility = checkWallMaterialCompatibility(equipment, userAnswers?.nature_murs);
                              return (
                                <button
                                  key={equipment.id}
                                  onClick={() => handleSelectEquipment(equipment.id)}
                                  disabled={isAdding}
                                  className={`w-full text-left p-4 border-2 rounded-lg hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                                    compatibility.level === 'danger'
                                      ? 'border-red-300 bg-red-50 hover:border-red-500'
                                      : 'border-gray-200 hover:border-green-500'
                                  }`}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <h4 className="font-semibold text-gray-900">
                                          {equipment.name}
                                        </h4>
                                        {equipment.is_default && (
                                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                                            Recommandé
                                          </span>
                                        )}
                                        {equipment.is_breathable === true && (
                                          <Info className="w-4 h-4 text-green-600" title="Matériau perspirant - Compatible avec tous types de murs" />
                                        )}
                                        {compatibility.level === 'danger' && (
                                          <AlertTriangle className="w-4 h-4 text-red-600" />
                                        )}
                                      </div>
                                      <p className="mt-1 text-sm text-gray-600">
                                        {equipment.description}
                                      </p>
                                      {compatibility.message && (
                                        <div className={`mt-2 p-2 rounded text-xs ${
                                          compatibility.level === 'danger'
                                            ? 'bg-red-100 text-red-800 border border-red-200'
                                            : 'bg-blue-50 text-blue-700'
                                        }`}>
                                          {compatibility.message}
                                        </div>
                                      )}
                                      <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500">
                                        {equipment.u_value !== null && (
                                          <span>U = {equipment.u_value.toFixed(2)} W/m².K</span>
                                        )}
                                        {equipment.r_value !== null && (
                                          <span>R = {equipment.r_value.toFixed(1)} m².K/W</span>
                                        )}
                                        {equipment.thickness_mm !== null && (
                                          <span>Épaisseur = {equipment.thickness_mm} mm</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ) : blockedCategories[selectedCategory]?.some(b => b.includes('ITI')) ? (
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm text-blue-800">
                            <strong>ITI non applicable :</strong> Contraintes techniques (boiseries, cuisine/SdB)
                          </p>
                        </div>
                      ) : null;
                    })()}
                  </div>
                ) : (
                  <>
                    {equipmentsByCategory?.[selectedCategory].map((equipment) => {
                      const showGasToElectricWarning = isGasToElectricRadiators(equipment);
                      // isAirAirOnHydraulicNetwork est appelé directement dans le JSX

                      return (
                        <div key={equipment.id}>
                          {isAirAirOnHydraulicNetwork(equipment) && (
                            <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <div className="flex items-start gap-2">
                                <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-blue-800">
                                  <strong>Remplacement du réseau de distribution requis :</strong> Votre installation actuelle (gaz/fioul) utilise des radiateurs à eau. Passer à une PAC air-air implique de remplacer ces radiateurs par des émetteurs air (splits, gainable). La PAC air-eau est l'alternative sans ce surcoût.
                                </p>
                              </div>
                            </div>
                          )}
                          {showGasToElectricWarning && (
                            <div className="mb-3 p-4 bg-amber-50 border-2 border-amber-300 rounded-lg">
                              <div className="flex items-start">
                                <AlertTriangle className="w-6 h-6 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                                <div>
                                  <h4 className="font-semibold text-amber-900 mb-2">
                                    Attention : Impact sur la consommation énergétique
                                  </h4>
                                  <p className="text-sm text-amber-800 mb-2">
                                    Les radiateurs électriques <strong>augmenteront votre consommation d'énergie finale d'environ 25%</strong>, même si les émissions de gaz à effet de serre diminuent grâce au mix électrique français.
                                  </p>
                                  <p className="text-sm text-amber-800 font-medium">
                                    💡 Recommandation : Une pompe à chaleur permet de réduire à la fois la consommation ET les émissions GES.
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          <button
                            onClick={() => handleSelectEquipment(equipment.id)}
                            disabled={isAdding}
                            className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h3 className="font-semibold text-gray-900">
                                  {equipment.name}
                                  {equipment.is_default && (
                                    <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                      Recommandé
                                    </span>
                                  )}
                                </h3>
                                <p className="mt-1 text-sm text-gray-600">
                                  {equipment.description}
                                </p>
                                <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500">
                                  {equipment.u_value !== null && (
                                    <span>Uw = {equipment.u_value.toFixed(2)} W/m².K</span>
                                  )}
                                  {equipment.r_value !== null && (
                                    <span>R = {equipment.r_value.toFixed(1)} m².K/W</span>
                                  )}
                                  {equipment.efficiency !== null && (
                                    <span>Rendement = {equipment.efficiency.toFixed(0)}%</span>
                                  )}
                                  {equipment.cop !== null && (
                                    <span>COP = {equipment.cop.toFixed(1)}</span>
                                  )}
                                  {equipment.thickness_mm !== null && (
                                    <span>Épaisseur = {equipment.thickness_mm} mm</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </button>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
